import type {
  VariableRefNode as VariableRefNodeType,
  Port,
} from "../../../../util/src/blueprint/types.js";
import { PortHandle } from "../PortHandle.js";
import dico from "../../../../util/src/language/dico.js";
import React, { useMemo } from "react";

export interface VariableRefNodeProps {
  node: VariableRefNodeType;
  setPortsMap: (
    nodeId: string,
    portId: string,
    element: HTMLElement | null,
  ) => void;
  onPortMouseDown?: (nodeId: string, port: Port) => void;
  onPortMouseUp?: (nodeId: string, port: Port) => void;
  onPortValueChange?: (nodeId: string, portId: string, value: unknown) => void;
  isPortConnected?: (nodeId: string, portId: string) => boolean;
  variableName?: string;
  outputValueType?: string;
}

type PortRow = { input?: Port; output?: Port };

function buildPortRows(ports: Port[]): PortRow[] {
  const inputs = ports.filter((port) => port.direction === "input");
  const outputs = ports.filter((port) => port.direction === "output");
  const rowCount = Math.max(inputs.length, outputs.length);
  return Array.from({ length: rowCount }, (_, index) => ({
    input: inputs[index],
    output: outputs[index],
  }));
}

/**
 * Renders a scene variable reference node with catalog-synced input/output ports.
 */
const VariableRefNodeComponent = ({
  node,
  setPortsMap,
  onPortMouseDown,
  onPortMouseUp,
  onPortValueChange,
  isPortConnected,
  variableName,
}: VariableRefNodeProps) => {
  const behaviorDico = dico().behavior;
  const sectionLabel = behaviorDico.sceneCatalog?.variableRef ?? "";
  const label =
    variableName?.trim() || behaviorDico.sceneCatalog?.panelEmpty || "-";
  const headerBg =
    "linear-gradient(180deg, #c4b5fd 0%, #7c3aed 55%, #5b21b6 100%)";
  const rows = useMemo(() => buildPortRows(node.ports), [node.ports]);

  const renderPort = (port: Port) => (
    <PortHandle
      node={node}
      port={port}
      setPortsMap={setPortsMap}
      onMouseDown={() => onPortMouseDown?.(node.id, port)}
      onMouseUp={() => onPortMouseUp?.(node.id, port)}
      isConnected={isPortConnected?.(node.id, port.id) ?? false}
      onValueChange={(value) => onPortValueChange?.(node.id, port.id, value)}
    />
  );

  return (
    <div
      style={{
        minWidth: 260,
        borderRadius: 8,
        overflow: "hidden",
        background:
          "linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(200, 200, 200, 0.98) 100%)",
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        border: "1px solid #e5e7eb",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          padding: "4px 8px",
          fontSize: 12,
          fontWeight: 700,
          color: "#fff",
          background: headerBg,
        }}
      >
        <span>{sectionLabel}</span>
        <span
          style={{
            fontWeight: 600,
            opacity: 0.95,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {label}
        </span>
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 6,
          padding: "8px",
        }}
        onMouseDown={(event) => event.stopPropagation()}
      >
        {rows.map((row, index) => (
          <div
            key={`var-ref-row-${index}`}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              minHeight: 28,
            }}
          >
            <div
              style={{ flex: 1, display: "flex", justifyContent: "flex-start" }}
            >
              {row.input ? renderPort(row.input) : null}
            </div>
            <div
              style={{ flex: 1, display: "flex", justifyContent: "flex-end" }}
            >
              {row.output ? renderPort(row.output) : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const VariableRefNode = React.memo(VariableRefNodeComponent);
